import { CaretDown, CaretUp, ChevronRight, Download } from '@carbon/icons-react'
import React, { useEffect, useMemo, useState } from 'react'
import {
  downloadSalesforcePermissionSetsReport,
  getSalesforcePermissionSets
} from '../../api/salesforceAudit/salesforceAudit.service'

import { Button } from '../../components/New/Button/Button'
import { DDSTypography } from 'den-design-system'
import NoRecord from '../../components/common/NoContent/NoRecord'
import NoRecordData from '../../assets/images/setting.svg'
import NoRecordImage from '../../assets/images/folder-not-found 1.svg'
import { SalesforceAuditStore } from '../../store/SalesforceAuditStore'
import Search from '../../components/New/Search/Search'
import Spinner from '../../components/common/Spinner/Spinner'
import Table from '../../components/common/DDS/Table/Table'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import Tooltip from '../../components/New/Tooltip/Tooltip'
import { convertJsonToCsvDownload } from '../../utils/FileUtils'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { timeFormats } from '../../enum/TIME'
import { timeZone } from '../../utils/DateTimeUtils'
import { useNavigate } from 'react-router-dom'
import { useStoreContext } from '../../store/StoreContext'

const SFPermissionSetsOverview: React.FC = () => {
  const navigate = useNavigate()
  const store = useStoreContext()
  const { salesforceAuditStore } = useStoreContext()
  const [currentPage, setCurrentPage] = useState(1)
  const [permissionSetLoading, setPermissionSetLoading] = useState(false)
  const [permissionSets, setPermissionSets] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [searchColumn, setSearchColumn] = useState('all')
  const [showDropdown, setShowDropdown] = useState(false)
  const [totalCount, setTotalCount] = useState<number>(0)

  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: 'asc' | 'desc' | null
  }>({
    key: null,
    direction: null
  })
  const toggleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  const salesforceAuditId = salesforceAuditStore.auditData?.salesforceAuditId

  useEffect(() => {
    const fetchPermissionSets = async () => {
      if (!salesforceAuditId) {
        setPermissionSetLoading(false)
        return
      }
      try {
        setPermissionSetLoading(true)
        const response = await getSalesforcePermissionSets({
          id: salesforceAuditId,
          page: currentPage,
          size: SalesforceAuditStore.pageSize
        })

        const permissionSets = response.data.data.map(
          (item: any) => item.record
        )
        salesforceAuditStore.setPermissionSets(response?.data?.data)
        setTotalCount(response?.data?.totalCount)
        setPermissionSets(permissionSets)
      } catch (err: any) {
        console.error('Error fetching profiles:', err)
      } finally {
        setPermissionSetLoading(false)
      }
    }

    fetchPermissionSets()
  }, [salesforceAuditId, currentPage])

  const filteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
    if (!trimmedSearch) return permissionSets

    return permissionSets.filter((item: any) => {
      const name = (item?.Name || '').toLowerCase()
      const description = (item?.Description || '').toLowerCase()

      let formattedDates: string[] = []
      if (item?.LastModifiedDate) {
        const m = moment(item.LastModifiedDate)
        formattedDates = [
          m.format('MMMM D YYYY').toLowerCase(),
          m.format('D MMMM YYYY').toLowerCase(),
          m.format('MMM D').toLowerCase(),
          m.format('D MMM').toLowerCase(),
          m.format('MMMM D').toLowerCase(),
          m.format('D MMMM').toLowerCase(),
          m.format('YYYY').toLowerCase(),
          m.format('D').toLowerCase(),
          m.format('MMMM').toLowerCase()
        ]
      }
      const modifiedBy = item?.LastModifiedBy?.Name?.toLowerCase() || ''

      const usersAssigned = item?.LastModifiedBy?.userCount

      const matchesDate = formattedDates.some((date) =>
        date.includes(trimmedSearch)
      )

      switch (searchColumn) {
        case 'Name':
          return name.includes(trimmedSearch)
        case 'Description':
          return description.includes(trimmedSearch)
        case 'LastModifiedDate':
          return matchesDate
        case 'LastModifiedById':
          return modifiedBy.includes(trimmedSearch)
        case 'Id':
          return usersAssigned.includes(trimmedSearch)
        case 'all':
        default:
          return (
            name.includes(trimmedSearch) ||
            description.includes(trimmedSearch) ||
            modifiedBy.includes(trimmedSearch) ||
            matchesDate ||
            usersAssigned.includes(trimmedSearch)
          )
      }
    })
  }, [permissionSets, activeSearchTerm, searchColumn, salesforceAuditStore])

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData
    return [...filteredData].sort((a: any, b: any) => {
      if (sortConfig.key === 'userCount') {
        const countA = a.userCount || 0
        const countB = b.userCount || 0

        if (countA < countB) return sortConfig.direction === 'asc' ? -1 : 1
        if (countA > countB) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      }

      if (sortConfig.key === 'LastModifiedDate') {
        const dateA = a.LastModifiedDate
          ? new Date(a.LastModifiedDate).getTime()
          : 0
        const dateB = b.LastModifiedDate
          ? new Date(b.LastModifiedDate).getTime()
          : 0
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA
      }
      const valA = a?.[sortConfig.key!] || ''
      const valB = b?.[sortConfig.key!] || ''
      if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
        return sortConfig.direction === 'asc'
          ? Number(valA) - Number(valB)
          : Number(valB) - Number(valA)
      }
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }
      return 0
    })
  }, [filteredData, sortConfig, salesforceAuditStore])

  useEffect(() => {
    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Salesforce Audit',
        link:
          replaceUrl(
            routeConstants.SALESFORCE_AUDIT_DASHBOARD,
            store.scopeStore.getScope()['organization-id'] as string,
            store.scopeStore.getScope()['project-id'] as string,
            store.scopeStore.getScope()['environment-id'] as string
          ) + '?page=1&tab=1'
      },
      {
        label: 'Permission Sets',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW +
            '/permission-sets',
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      }
    ])
  }, [store.breadcrumbStore.getBreadcrumbsOptions()])

  const downloadPermissionSets = async () => {
    store.uiStore.setGlobalLoader(true)
    setPermissionSetLoading(true)

    const payload = {
      salesforceAuditId: salesforceAuditId as string
    }

    try {
      const response = await downloadSalesforcePermissionSetsReport(payload)
      const orgTZ = store.userStore.getOrganizationPreference().timeZone
      const permissionSets: any[] = response.data || []

      if (!permissionSets.length) {
        ToastNotification({
          type: 'error',
          message: 'No Permission Sets found for this audit'
        })
        return
      }

      const mapped = permissionSets.map((ps) => {
        return {
          PermissionSetName: ps?.name || '-',
          User_Assigned: ps?.userCount || '0',
          Label: ps?.label || '-',
          Description: ps?.description || '-',
          LastModifiedDate:
            ps?.lastModifiedDate && ps?.lastModifiedDate !== '-'
              ? timeZone(
                  orgTZ,
                  ps?.lastModifiedDate,
                  timeFormats.fullMonthDateYearTime
                )
              : '-',
          LastModifiedBy: ps?.lastModifiedByName || '-'
        }
      })

      const date = new Date()
      const formattedDate = `${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${date
        .getDate()
        .toString()
        .padStart(2, '0')}-${date.getFullYear()}`

      convertJsonToCsvDownload(
        mapped,
        `Salesforce_PermissionSets_Report_${formattedDate}.csv`
      )

      ToastNotification({
        type: 'success',
        message: `Successfully exported ${mapped.length} Permission Set(s)`
      })
    } catch (error: any) {
      console.error('Permission Set Download Error:', error)

      ToastNotification({
        type: 'error',
        message:
          typeof error === 'string'
            ? error
            : error?.message || 'Error downloading Permission Sets report'
      })
    } finally {
      setPermissionSetLoading(false)
      store.uiStore.setGlobalLoader(false)
    }
  }

  const Columns = [
    {
      columnHeaderStyle: { width: '200px' },
      columnDataStyle: { width: '200px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Permission Set Name
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Name',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <Tooltip
            id={`${text}-tooltip`}
            label={text}
            className={`override-tooltip-arrow max-w-[90%] break-all ${
              typeof text === 'string' && text.length > 24 ? text : '!hidden'
            }`}
          >
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='truncate max-w-[200px]'
              color={pallete.colors.textDark4}
            >
              {text ?? '-'}
            </DDSTypography.Paragraph>
          </Tooltip>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Users Assigned
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('userCount')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'userCount' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'userCount' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'userCount',
      render: (text: any) => {
        return (
          <div className='flex justify-left text-left row items-left'>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              color={pallete.colors.textDark4}
              className='tooltip-container sm:block overflow-hidden truncate'
            >
              {text}
            </DDSTypography.Paragraph>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: { width: '200px' },
      columnDataStyle: { width: '200px' },
      heading: (
        <div className='flex whitespace-nowrap text-left justify-left space-x-[8px] items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Description
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Description')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Description' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Description' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Description',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <Tooltip
            id={`${text}-tooltip`}
            label={text}
            className={`override-tooltip-arrow max-w-[90%] break-all ${
              typeof text === 'string' && text.length > 20 ? text : '!hidden'
            }`}
          >
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='truncate min-w-[200px] max-w-[200px]'
              color={pallete.colors.textDark4}
            >
              {text ?? '-'}
            </DDSTypography.Paragraph>
          </Tooltip>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap text-left justify-left space-x-[8px] items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Last Modified
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedDate')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedDate',
      render: (text: any) => (
        <div className='flex justify-left text-left row items-left'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {text
              ? moment(text).format(timeFormats.shortMonthDateYearTime)
              : '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap text-left justify-left space-x-[8px] items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Modified by
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedById')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedById' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedById' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedBy',
      render: (record: any) => {
        const lastModifiedByName = record?.Name || '-'

        return (
          <div className='flex justify-start text-start items-start'>
            <Tooltip
              id={`tooltip-name-${lastModifiedByName}`}
              label={lastModifiedByName}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof lastModifiedByName === 'string' &&
                lastModifiedByName.length > 16
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
                >
                  {lastModifiedByName}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: { width: '20px' },
      columnDataStyle: { width: '20px' },
      heading: '',
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='cursor-pointer'>
            <ChevronRight
              size={16}
              onClick={() => {
                navigate(text)
              }}
              color={pallete.colors.FillButton}
            />
          </div>
        )
      }
    }
  ]
  return (
    <>
      {permissionSetLoading || store.uiStore.getGlobalLoader() ? (
        <Spinner />
      ) : (
        <div className='flex flex-col w-full py-[24px]'>
          <div className='relative pb-[12px]'>
            <Search
              id='search'
              name='Search'
              placeholder='Search'
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value
                setSearchTerm(value)
                setShowDropdown(value.length > 0)
                if (value.trim() === '') {
                  setActiveSearchTerm('')
                }
              }}
              autoComplete='off'
              width='100%'
              onPressEnter={() => {
                setActiveSearchTerm(searchTerm.trim())
                setCurrentPage(1)
                setShowDropdown(false)
              }}
              onBlur={() => {
                setTimeout(() => {
                  setShowDropdown(false)
                  setCurrentPage(1)
                }, 200)
              }}
            />

            {showDropdown && (
              <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                {[
                  { label: 'Name', value: 'Name' },
                  { label: 'User Assigned', value: 'Id' },
                  { label: 'Description', value: 'Description' },
                  { label: 'Last Modified Date', value: 'LastModifiedDate' },
                  { label: 'All Columns', value: 'all' }
                ].map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      setSearchColumn(option.value)
                      setCurrentPage(1)
                      setActiveSearchTerm(searchTerm)
                      setShowDropdown(false)
                    }}
                    className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                  >
                    <span className='font-mulish text-black'>{searchTerm}</span>
                    <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                      in: {option.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className='w-full flex justify-end mb-[16px]'>
            <Button
              id='outline-button'
              label='Export'
              disabled={
                store.uiStore.getGlobalLoader() || permissionSets?.length === 0
              }
              type='neutral'
              size='small'
              onClick={() => {
                downloadPermissionSets()
              }}
              startIcon={<Download color={pallete.colors.neuButtonText} />}
            />
          </div>
          {permissionSets?.length > 0 ? (
            <Table
              columns={Columns}
              data={sortedData}
              totalCount={totalCount}
              currentPage={currentPage}
              rowsPerPage={SalesforceAuditStore.pageSize}
              onPageChange={(page) => setCurrentPage(page)}
              clickable={(id) => {
                navigate(id)
              }}
              clickType='SalesforcePermissionSet'
            />
          ) : searchTerm?.trim() ? (
            <NoRecord
              imageSrc={NoRecordImage}
              style={{ height: '45vh' }}
              text={<div>No Permission Sets match your search.</div>}
            />
          ) : (
            <NoRecord
              imageSrc={NoRecordData}
              style={{ height: '45vh' }}
              text={<div>No Permission Sets have been configured yet</div>}
            />
          )}
        </div>
      )}
    </>
  )
}

export default observer(SFPermissionSetsOverview)
